// Companies only. Shared table methods are in tableMixin.js.
export default {
    name: "Companies",
    methods: {
        /* ---------------- Brand colours ---------------- */
        // Default brand colours (same as pdfDefaultBrandColors() in global_functions.php)
        brandDefaults() {
            return {
                brand_primary: "#1B2A41",
                brand_accent: "#B8913A",
                brand_background: "#4A5A6E",
                brand_paper: "#F5F2EA",
                brand_template: "classic",
            };
        },
        // Colour helpers (same rules as the PHP PDF functions, so the preview matches the PDF)
        brandRgb(hex) {
            return [1, 3, 5].map((i) => parseInt(String(hex || "#000000").slice(i, i + 2), 16) || 0);
        },
        brandContrast(a, b) {
            const lum = (hex) => {
                const c = this.brandRgb(hex).map((v) => {
                    v /= 255;
                    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
                });
                return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
            };
            const x = lum(a), y = lum(b);
            return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
        },
        brandMix(a, b, t) {
            const B = this.brandRgb(b);
            return "#" + this.brandRgb(a).map((v, i) => Math.round(v + (B[i] - v) * t).toString(16).padStart(2, "0")).join("");
        },
        brandBestOf(bg) {
            return this.brandContrast("#FFFFFF", bg) >= this.brandContrast("#1A1A1A", bg) ? "#FFFFFF" : "#1A1A1A";
        },
        // Table heading text on the Primary colour
        brandHeadingTextColor() {
            const bg = this.formInputs.brand_primary;
            const tint = this.brandMix(this.formInputs.brand_accent, "#FFFFFF", 0.55);
            return this.brandContrast(tint, bg) >= 4.5 ? tint : this.brandBestOf(bg);
        },
        // Is the paper dark? (rows then stay dark instead of turning grey)
        brandPaperIsDark() {
            const [r, g, b] = this.brandRgb(this.formInputs.brand_paper);
            return (0.299 * r + 0.587 * g + 0.114 * b) < 150;
        },
        brandRowColor(light) {
            const dark = this.brandPaperIsDark();
            const amount = dark ? (light ? 0.10 : 0.04) : (light ? 0.55 : 0.25);
            return this.brandMix(this.formInputs.brand_paper, "#FFFFFF", amount);
        },
        // Text on the paper: primary colour, or white/near-black if it can't be read
        brandSheetTextColor() {
            const p = this.formInputs.brand_primary;
            const [r, g, b] = this.brandRgb(p);
            let text = (0.299 * r + 0.587 * g + 0.114 * b) < 150 ? p : this.brandMix(p, "#000000", 0.72);
            const row = this.brandRowColor(false);
            return this.brandContrast(text, row) >= 4.5 ? text : this.brandBestOf(row);
        },
        // Template name for the summary badge in the company form
        brandTemplateLabel() {
            const t = (this.brandTemplates || []).find((x) => x.value === this.formInputs.brand_template);
            return t ? t.label.split(" — ")[0] : "Classic";
        },
        resetBrandColors() {
            Object.assign(this.formInputs, this.brandDefaults());
        },
        // Typed colour: add "#", expand #abc to #aabbcc; anything invalid goes back to the default
        normalizeBrandColor(key) {
            let value = String(this.formInputs[key] || "").trim().replace(/^#?/, "#");
            if (/^#[0-9a-f]{3}$/i.test(value)) {
                value = "#" + value.slice(1).split("").map((c) => c + c).join("");
            }
            this.formInputs[key] = /^#[0-9a-f]{6}$/i.test(value)
                ? value.toUpperCase()
                : this.brandDefaults()[key];
        },

        /* ---------------- Form ---------------- */
        openDatePicker() {
            this.$refs.dateInput.showPicker?.(); // supported in Chromium-based browsers
        },
        clearForm() {
            this.isEditing = false;
            this.isSaving = false;
            this.submitted = false;

            this.formInputs.id = "";

            this.formInputs.company_number = "";
            this.formInputs.legacy_source = "";
            this.formInputs.legacy_id = "";

            this.formInputs.owner_user_id = "";
            this.formInputs.company_id = "";
            this.formInputs.branch_id = "";

            this.formInputs.company_name = "";
            this.formInputs.registration_number = "";
            this.formInputs.tax_number = "";
            this.formInputs.email = "";
            this.formInputs.phone = "";
            this.formInputs.website = "";

            this.formInputs.current_location = "";

            this.formInputs.address = "";
            this.formInputs.city = "";
            this.formInputs.state = "";
            this.formInputs.country = "";
            this.formInputs.postal_code = "";
            this.formInputs.currency_code = "";
            this.formInputs.timezone = "";

            Object.assign(this.formInputs, this.brandDefaults());

            this.formInputs.logo = "";
            this.formInputs.is_active = true;

            this.formInputs.file = null;

            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = "";
            }

            this.$clearValidationStates("#recordForm");
        },
    },
};